/**
 * @format
 */

import { push } from '@lagunovsky/redux-react-router';
import moment from 'moment';
import AppointmentCancelView from './views/appointment/appointmentCancelView';
import AppointmentDetailsView from './views/appointment/appointmentDetailsView';
import AppointmentEditView from './views/appointment/appointmentEditView';
import AppointmentRescheduleView from './views/appointment/appointmentRescheduleView';
import AvailabilitySearchView from './features/searchAsync/availabilitySearchView';
import NotFound from './components/NotFound';
import InstanceUnavailable from './components/common/instanceUnavailable';
import PermissionWarning from './components/common/permissionWarning';
import LandingPageSelector from './components/landingPageSelector';
import mhd2Permissions from '@/constants/permissions';
import IdlePopupContainer from './utilities/idleTimer/idleTimerContainter';
import BookAppointmentView from './views/bookAppointment/bookAppointmentView';
import DecisionSupportView from './views/decisionSupport/decisionSupportView';
import CreateOrderView from './views/orderManagement/createOrderView';
import SearchOrdersView from './views/orderManagement/searchOrdersView';
import PatientAddView from './views/patient/patientAddView';
import PatientDetailsView from './features/patient/patientDetailsView';
import PatientEditView from './views/patient/patientEditView';
import PatientSearchView from './views/patient/patientSearchView';
import SingleSignOnRedirector from './views/redirectors/singleSignOnRedirector';
import StartCreateCareOrderRedirector from './views/redirectors/startCreateCareOrderRedirector';
import PatientRedirector from './views/redirectors/patientRedirector';
import AppointmentRedirector from './views/redirectors/appointmentRedirector';
import LocationRedirector from './views/redirectors/locationRedirector';
import StartAppointmentFirstRedirector from './views/redirectors/startAppointmentFirstRedirector';
import RequestAppointmentDetailsView from './views/requestAppointment/requestAppointmentDetailsView';
import RequestAppointmentView from './views/requestAppointment/requestAppointmentView';

import Login from './features/user/loginView';
import Logout from './features/user/logoutView';
import SsoAccessDenied from './features/user/ssoAccessDeniedView';
import FirstLoginView from './features/user/firstLoginView';
import ForgotPasswordView from './features/user/forgotPasswordView';
import ResetPasswordView from './features/user/resetPasswordView';
import UserProfileView from './features/user/userProfileView';
import SearchAppointmentsView from './features/searchAppointments/searchAppointmentsView';
import CalendarScheduleView from './features/calendarSchedule/calendarScheduleView';

import { RouteRenderer } from 'components/routes';

moment.defaultFormat = 'YYYY-MM-DDTHH:mm:ss';

/* * * WARNING - All changes to routes must update the Rd2RouterSwitch, URL Builder and/or Action method * * */

const routes = [
    // Public Routes
    { path: ['/login', '/:routePrefix/login'], element: <Login />, publicAccess: true },
    { path: ['/logout', '/:routePrefix/logout'], element: <Logout />, publicAccess: true },
    { path: ['/forgotpassword', '/:routePrefix/forgotpassword'], element: <ForgotPasswordView />, publicAccess: true },
    { path: ['/resetpassword', '/:routePrefix/resetpassword'], element: <ResetPasswordView />, publicAccess: true },
    { path: ['/external/login/:token', '/:routePrefix/external/login/:token'], element: <SingleSignOnRedirector />, publicAccess: true },
    { path: ['/accessdenied', '/:routePrefix/accessdenied'], element: <PermissionWarning />, publicAccess: true },
    { path: ['/', '/:routePrefix/'], element: <LandingPageSelector />, permission: false },
    {
        path: [
            '/appointment/:referenceId/cancel',
            '/:routePrefix/appointment/:referenceId/cancel',
        ],
        element: <AppointmentCancelView />,
        permission: mhd2Permissions.appointmentManagement,
    },
    {
        path: [
            '/appointment/:referenceId/details',
            '/:routePrefix/appointment/:referenceId/details',
        ],
        element: <AppointmentDetailsView />,
        permission: mhd2Permissions.appointmentView,
    },
    {
        path: [
            '/cancelledAppointment/:referenceId/details',
            '/:routePrefix/cancelledAppointment/:referenceId/details',
        ],
        element: <AppointmentDetailsView isCancelled={true} />,
        permission: mhd2Permissions.appointmentView,
    },
    {
        path: [
            '/appointment/:referenceId/edit',
            '/:routePrefix/appointment/:referenceId/edit',
        ],
        element: <AppointmentEditView />,
        permission: mhd2Permissions.appointmentManagement,
    },
    {
        path: [
            '/appointment/:referenceId/reschedule',
            '/:routePrefix/appointment/:referenceId/reschedule',
        ],
        element: <AppointmentRescheduleView />,
        permission: mhd2Permissions.appointmentManagement,
    },
    {
        path: [
            '/appointment/request/:requestId/details',
            '/:routePrefix/appointment/request/:requestId/details',
        ],
        element: <RequestAppointmentDetailsView />,
        permission: mhd2Permissions.appointmentView,
    },
    {
        path: [
            '/availability/search',
            '/:routePrefix/availability/search',
        ],
        element: <AvailabilitySearchView />,
        permission: mhd2Permissions.appointmentManagement,
    },
    {
        path: [
            '/availability/start',
            '/:routePrefix/availability/start',
        ],
        element: <StartAppointmentFirstRedirector />,
        permission: mhd2Permissions.appointmentManagement,
    },
    {
        path: [
            '/decisionSupport',
            '/:routePrefix/decisionSupport',
        ],
        element: <DecisionSupportView />,
        permission: mhd2Permissions.appointmentManagement,
    },
    {
        path: [
            '/orders/create',
            '/:routePrefix/orders/create',
        ],
        element: <CreateOrderView />,
        permission: mhd2Permissions.orderManagement,
    },
    {
        path: [
            '/orders/search',
            '/:routePrefix/orders/search',
        ],
        element: <SearchOrdersView />,
        permission: mhd2Permissions.orderView,
    },
    {
        path: [
            '/searchAppointments',
            '/:routePrefix/searchAppointments',
        ],
        element: <SearchAppointmentsView />,
        permission: mhd2Permissions.appointmentView,
    },
    {
        path: [
            '/calendarSchedule',
            '/:routePrefix/calendarSchedule',
        ],
        element: <CalendarScheduleView />,
        permission: mhd2Permissions.calendarScheduleView,
    },
    {
        path: [
            '/patient/:referenceId/book/:availabilityId',
            '/:routePrefix/patient/:referenceId/book/:availabilityId',
        ],
        element: <BookAppointmentView />,
        permission: mhd2Permissions.appointmentManagement,
    },
    {
        path: [
            '/patient/search',
            '/:routePrefix/patient/search',
        ],
        element: <PatientSearchView />,
        permission: mhd2Permissions.patientView,
    },
    {
        path: [
            '/patient/create',
            '/:routePrefix/patient/create',
        ],
        element: <PatientAddView />,
        permission: mhd2Permissions.patientManagement,
    },
    {
        path: [
            '/patient/:referenceId/details',
            '/:routePrefix/patient/:referenceId/details',
        ],
        element: <PatientDetailsView />,
        permission: mhd2Permissions.patientView,
    },
    {
        path: [
            '/patient/:referenceId/edit',
            '/:routePrefix/patient/:referenceId/edit',
        ],
        element: <PatientEditView />,
        permission: mhd2Permissions.patientManagement,
    },
    {
        path: [
            '/provider/:serviceSiteId/appointment/request',
            '/:routePrefix/provider/:serviceSiteId/appointment/request'
        ],
        element: <RequestAppointmentView />
    },
    {
        path: [
            '/user/profile',
            '/:routePrefix/user/profile'
        ],
        element: <UserProfileView />,
        permission: false
    },
    {
        path: [
            '/user/firstlogin',
            '/:routePrefix/user/firstlogin'
        ],
        element: <FirstLoginView />,
        permission: false
    },
    {
        path: [
            '/start/order/create',
            '/:routePrefix/start/order/create'
        ],
        element: <StartCreateCareOrderRedirector />,
        permission: false
    },
    {
        path: [
            '/start/patient/:refValue',
            '/:routePrefix/start/patient/:refValue'
        ],
        element: <PatientRedirector />,
        permission: false
    },
    {
        path: [
            '/start/appointment/:refValue',
            '/:routePrefix/start/appointment/:refValue'
        ],
        element: <AppointmentRedirector />,
        permission: false
    },
    {
        path: [
            '/start/location',
            '/:routePrefix/start/location'
        ],
        element: <LocationRedirector />,
        permission: mhd2Permissions.patientView,
    },
    { path: '*', element: <NotFound />, permission: false },
];

/* Rd2RouteSwitch - builds the react router switch component for RD2 routes */
export function Rd2RouteSwitch(props) {
    const { instance, location, isAuthenticated } = props;

    if (instance && instance.isInstanceUnavailable && !location.pathname.includes('/logout')) {
        return <InstanceUnavailable message={instance.instanceUnavailableText} />;
    }


    return (
        <>
            {isAuthenticated && <IdlePopupContainer />}
            <RouteRenderer routes={routes} />
        </>
    );
}

export function getRoutePaths() {
    return routes
        .filter((route) => {
            if (!route.path || !Array.isArray(route.path)) {
                console.warn('Invalid route path:', route);
                return false;
            }
            return true;
        })
        .flatMap((route) =>
            route.path
                .filter((path) => !path.startsWith('/:routePrefix'))
                .map((path) => {
                    const segments = path.replace(/^\/|\/$/g, '').split('/');
                    const firstSegment = segments[0] || '';
                    return firstSegment.split(':')[0];
                })
        )
        .filter((path) => path !== '' && path !== '*')
        .filter((value, index, self) => self.indexOf(value) === index);
}

export function menuItems(props) {
    const enableAppointmentSearch = props.config?.appointmentManagement?.enableAppointmentSearch;
    const enableCalendarSchedule = props.config?.appointmentManagement?.enableCalendarSchedule;
    const enableAppointmentFirstScheduling = props.config?.availabilitySearch?.enableAppointmentFirstScheduling;
    let menuItems = [
        {
            name: 'Search Patient',
            requiredPermission: mhd2Permissions.patientView,
            action: patientSearchUrl(props.instance.routePrefix),
            requiresProductInstanceSetting: false,
        },
        {
            name: 'Create Order',
            requiredPermission: mhd2Permissions.orderManagement,
            action: createCareOrderUrl(props.instance.routePrefix),
            requiresActivePatient: 'true',
            requiresProductInstanceSetting: false,
        },
        {
            name: 'Search Orders',
            requiredPermission: mhd2Permissions.orderView,
            action: searchOrdersUrl(props.instance.routePrefix),
            requiresProductInstanceSetting: false,
        },
        {
            name: 'Search Appointments',
            requiredPermission: mhd2Permissions.appointmentView,
            action: searchAppointmentsUrl(props.instance.routePrefix),
            requiresProductInstanceSetting: true,
            productInstanceSettingEnabled: enableAppointmentSearch,
        },
        {
            name: 'Calendar Schedule',
            requiredPermission: mhd2Permissions.calendarScheduleView,
            action: calendarScheduleUrl(props.instance.routePrefix),
            requiresProductInstanceSetting: true,
            productInstanceSettingEnabled: enableCalendarSchedule,
        }
    ];
    if (enableAppointmentFirstScheduling) {
        menuItems.splice(1, 0, {
            name: 'Search Availability',
            requiredPermission: mhd2Permissions.appointmentManagement,
            action: appointmentFirstStartUrl(props.instance.routePrefix),
            requiresProductInstanceSetting: false,
        });
    }
    return menuItems;
}

/* Route Methods */
export function loginUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/login` : '/login';
}

export function login(routePrefix) {
    return push(loginUrl(routePrefix));
}

export function firstLoginUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/user/firstlogin` : '/user/firstLogin'
}

export function firstLogin(routePrefix) {
    return push(firstLoginUrl(routePrefix));
}

export function createCareOrderUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/orders/create` : '/orders/create';
}

export function createCareOrder(routePrefix) {
    return push(createCareOrderUrl(routePrefix));
}

export function forgotPasswordUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/forgotpassword` : '/forgotpassword';
}

export function forgotPassword(routePrefix) {
    return push(forgotPasswordUrl(routePrefix));
}

export function searchAppointmentsUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/searchAppointments` : '/searchAppointments';
}

export function searchAppointments(routePrefix) {
    return push(searchAppointmentsUrl(routePrefix));
}

export function calendarScheduleUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/calendarSchedule` : '/calendarSchedule';
}

export function calendarSchedule(routePrefix) {
    return push(calendarScheduleUrl(routePrefix));
}

export function patientAddUrl(routePrefix, firstName = '', lastName = '', dateOfBirth, memberId = '') {
    dateOfBirth = dateOfBirth ? moment(dateOfBirth).format('YYYY-MM-DD') : '';
    const baseUrl = routePrefix ? `/${routePrefix}/patient/create` : '/patient/create';
    return `${baseUrl}?firstName=${firstName}&lastName=${lastName}&dateOfBirth=${dateOfBirth}&memberId=${memberId}`;
}

export function patientAdd(routePrefix, firstName = '', lastName = '', dateOfBirth, memberId = '') {
    return push(patientAddUrl(routePrefix, firstName, lastName, dateOfBirth, memberId));
}

export function patientSearchUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/patient/search` : '/patient/search';
}

export function appointmentFirstStartUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/availability/start` : '/availability/start';
}

export function patientSearch(routePrefix) {
    return push(patientSearchUrl(routePrefix));
}

// Appointment-first hands off to patient search mid-flow, with a reserved slot that has to survive
// the trip. The marker lets patient search tell that handoff apart from every other arrival, and it
// rides in the URL so that a refresh keeps it while any plain navigation here drops it.
export function patientSearchAppointmentFirstHandoff(routePrefix) {
    return push(`${patientSearchUrl(routePrefix)}?appointmentFirst=1`);
}

export function patientDetailsUrl(routePrefix, referenceId) {
    return routePrefix ? `/${routePrefix}/patient/${referenceId}/details` : `/patient/${referenceId}/details`;
}

export function patientDetails(routePrefix, referenceId) {
    return push(patientDetailsUrl(routePrefix, referenceId));
}

export function patientEditUrl(routePrefix, referenceId) {
    return routePrefix ? `/${routePrefix}/patient/${referenceId}/edit` : `/patient/${referenceId}/edit`;
}

export function patientEdit(routePrefix, referenceId) {
    return push(patientEditUrl(routePrefix, referenceId));
}

export function availabilitySearchUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/availability/search` : '/availability/search';
}

export function availabilitySearch(routePrefix) {
    return push(availabilitySearchUrl(routePrefix));
}

export function appointmentDetailsUrl(routePrefix, referenceId) {
    return routePrefix ? `/${routePrefix}/appointment/${referenceId}/details` : `/appointment/${referenceId}/details`;
}

export function cancelledAppointmentDetailsUrl(routePrefix, referenceId) {
    return routePrefix ? `/${routePrefix}/cancelledAppointment/${referenceId}/details` : `/cancelledAppointment/${referenceId}/details`;
}

export function appointmentEditUrl(routePrefix, referenceId) {
    return routePrefix ? `/${routePrefix}/appointment/${referenceId}/edit` : `/appointment/${referenceId}/edit`;
}

export function appointmentEdit(routePrefix, referenceId) {
    return push(appointmentEditUrl(routePrefix, referenceId));
}

export function rescheduleAppointmentUrl(routePrefix, referenceId) {
    return routePrefix ? `/${routePrefix}/appointment/${referenceId}/reschedule` : `/appointment/${referenceId}/reschedule`;
}

export function cancelAppointmentUrl(routePrefix, referenceId) {
    return routePrefix ? `/${routePrefix}/appointment/${referenceId}/cancel` : `/appointment/${referenceId}/cancel`;
}

export function requestAppointmentUrl(routePrefix, serviceSiteId) {
    return routePrefix ? `/${routePrefix}/provider/${serviceSiteId}/appointment/request` : `/provider/${serviceSiteId}/appointment/request`;
}

export function requestAppointmentDetailsUrl(routePrefix, requestId) {
    return routePrefix ? `/${routePrefix}/appointment/request/${requestId}/details` : `/appointment/request/${requestId}/details`;
}

export function appointmentDetails(routePrefix, referenceId) {
    return push(appointmentDetailsUrl(routePrefix, referenceId));
}

export function cancelledAppointmentDetails(routePrefix, referenceId) {
    return push(cancelledAppointmentDetailsUrl(routePrefix, referenceId));
}

export function rescheduleAppointment(routePrefix, referenceId) {
    return push(rescheduleAppointmentUrl(routePrefix, referenceId));
}

export function cancelAppointment(routePrefix, referenceId) {
    return push(cancelAppointmentUrl(routePrefix, referenceId));
}

export function startBookingProcessUrl(routePrefix, useDecisionSupport) {
    return useDecisionSupport ? decisionSupportUrl(routePrefix) : availabilitySearchUrl(routePrefix);
}

export function startBookingProcess(routePrefix, useDecisionSupport) {
    return push(startBookingProcessUrl(routePrefix, useDecisionSupport));
}

export function decisionSupportUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/decisionSupport` : '/decisionSupport';
}

export function decisionSupport(routePrefix) {
    return push(decisionSupportUrl(routePrefix));
}

export function logoutUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/logout` : '/logout';
}

export function logoutUser(routePrefix) {
    return push(logoutUrl(routePrefix));
}

export function createOrderUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/orders/create` : '/orders/create';
}

export function createOrder(routePrefix) {
    return push(createOrderUrl(routePrefix));
}

export function searchOrdersUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/orders/search` : '/orders/search';
}

export function bookAppointmentUrl(routePrefix, patientReferenceId, timeSlotId) {
    return routePrefix ? `/${routePrefix}/patient/${patientReferenceId}/book/${timeSlotId}` : `/patient/${patientReferenceId}/book/${timeSlotId}`;
}

export function searchOrders(routePrefix) {
    return push(searchOrdersUrl(routePrefix));
}

export function requestAppointment(routePrefix, serviceSiteId) {
    return push(requestAppointmentUrl(routePrefix, serviceSiteId));
}

export function requestAppointmentDetails(routePrefix, requestId) {
    return push(requestAppointmentDetailsUrl(routePrefix, requestId));
}

export function routeToBooking(routePrefix, patientReferenceId, timeSlotId) {
    return push(bookAppointmentUrl(routePrefix, patientReferenceId, timeSlotId));
}

export function userProfileUrl(routePrefix) {
    return routePrefix ? `/${routePrefix}/user/profile` : '/user/profile';
}